<div class="fibre-section">

  <div *ngIf="loading" class="py-5 text-center">
    <div class="spinner-border spinner-border-sm" role="status">
      <span class="sr-only">Loading...</span>
    </div>
    <div class="mt-2">Loading fibre pricing…</div>
  </div>

  <div *ngIf="errorMessage" class="alert alert-warning">
    {{ errorMessage }}
  </div>

  <ng-container *ngIf="!loading && !errorMessage">

    <h4 class="mb-4">Fibre Service Application</h4>

    <ng-container *ngIf="!submitted">

    <section class="mb-4">
      <h5>Instructions</h5>
      <ol>
        <li>Select your preferred ISP and fibre internet line speed from the options below.</li>
        <li>Your request will be submitted to your selected ISP to formalise your application.</li>
        <li>Customer details may be amended or reassigned to another individual with the ISP at a later stage, subject to the ISP's policies and procedures.</li>
      </ol>
    </section>

    <ng-container *ngIf="!autoRedirectOptOut">

    <section class="mb-4">
      <h5>Package Options</h5>

      <div *ngIf="rows.length === 0" class="alert alert-info">
        No fibre offerings are available for this development yet.
      </div>

      <div *ngIf="rows.length > 0" class="table-responsive">
        <table class="table table-bordered pricing-grid mb-0">
          <thead class="table-light">
            <tr>
              <th>ISP</th>
              <th *ngFor="let speed of speedTiers" class="text-center">{{ speed }} Mb/s</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of rows">
              <td class="fw-bold">{{ row.isp }}</td>
              <td *ngFor="let speed of speedTiers"
                  class="text-center price-cell"
                  [class.selectable]="row.prices[speed] !== null"
                  [class.selected]="isSelected(row.isp, speed)"
                  (click)="selectOffering(row.isp, speed, row.prices[speed])">
                <ng-container *ngIf="row.prices[speed] !== null; else emptyCell">
                  {{ row.prices[speed] | currency:'R ':'symbol':'1.0-0' }}
                </ng-container>
                <ng-template #emptyCell><span class="text-muted">—</span></ng-template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="notes mb-4">
      <div class="disclaimer">
        <h6 class="disclaimer-heading">Please note</h6>
        <ul class="mb-0">
          <li>Prices are determined by each ISP and are subject to change.</li>
          <li>Installation and activation fees may apply depending on the ISP.</li>
        </ul>
      </div>
    </section>

    </ng-container>

    <div class="fibre-optout">
      <label class="optout-control">
        <input type="checkbox" #optOut
               [checked]="autoRedirectOptOut"
               (change)="onOptOutChange(optOut.checked)">
        <span>Not interested in Fibre</span>
      </label>
    </div>

    <p class="optout-hint" *ngIf="autoRedirectOptOut">
      Changed your mind? Untick the box to see the fibre options again.
    </p>

    </ng-container>

    <section *ngIf="selectedOffering || submitted" class="customer-form" #customerFormRef>

      <ng-container *ngIf="!submitted">
        <h5>Customer Details</h5>

        <div class="selected-summary mb-3">
          <strong>Selected:</strong>
          {{ selectedOffering.isp }} — {{ selectedOffering.speed }} Mb/s —
          {{ selectedOffering.price | currency:'R ':'symbol':'1.0-0' }} / month
        </div>

        <form [formGroup]="applicationForm" (ngSubmit)="onSubmit()">

          <div class="form-group mb-3">
            <label for="fibreCustomerName">Customer Name</label>
            <input id="fibreCustomerName" type="text" class="form-control"
                   formControlName="customerName">
            <div *ngIf="applicationForm.get('customerName').invalid && applicationForm.get('customerName').touched"
                 class="text-danger small">
              Customer name is required.
            </div>
          </div>

          <div class="form-group mb-3">
            <label for="fibreDevelopmentUnit">Development Name &amp; Unit</label>
            <input id="fibreDevelopmentUnit" type="text" class="form-control"
                   formControlName="developmentUnit">
          </div>

          <div class="form-group mb-3">
            <label for="fibreEmail">Email</label>
            <input id="fibreEmail" type="email" class="form-control"
                   formControlName="email">
            <div *ngIf="applicationForm.get('email').invalid && applicationForm.get('email').touched"
                 class="text-danger small">
              A valid email is required.
            </div>
          </div>

          <div class="form-group mb-3">
            <label for="fibreMobile">Mobile</label>
            <input id="fibreMobile" type="text" class="form-control"
                   formControlName="mobile">
            <div *ngIf="applicationForm.get('mobile').invalid && applicationForm.get('mobile').touched"
                 class="text-danger small">
              Mobile number is required.
            </div>
          </div>

          <div class="form-check mb-2">
            <input id="fibreConsent" type="checkbox" class="form-check-input"
                   formControlName="consent">
            <label class="form-check-label" for="fibreConsent">
              I consent to being contacted by the ISP's sales team to assist with signing up for fibre at my home.
            </label>
          </div>

          <div class="mb-3">
            <button type="button" class="btn btn-link px-0 popia-toggle" (click)="togglePopia()">
              {{ showPopia ? 'Hide' : 'View' }} full POPIA consent
            </button>

            <div *ngIf="showPopia" class="popia-block mt-2 p-3">
              <p>
                I, the undersigned, hereby provide my voluntary and informed consent to
                <strong>Craft Homes</strong> to collect, process, store, and share my personal information,
                including my contact details, with a third-party internet service provider
                <strong>solely for the purpose of facilitating a fibre connection</strong> to my unit.
              </p>
              <p class="mb-1">I acknowledge that:</p>
              <ol class="mb-0">
                <li>
                  My personal information will be processed in accordance with the
                  <strong>Protection of Personal Information Act, 4 of 2013 (POPIA)</strong>.
                </li>
                <li>
                  Craft Homes will take reasonable steps to secure and protect my personal information
                  against unauthorised access, loss, or misuse.
                </li>
                <li>
                  My information will only be shared with the relevant third-party service provider
                  for the purpose stated above.
                </li>
                <li>
                  I may, at any time, request access to, correction of, or deletion of my personal
                  information held by Craft Homes, or withdraw my consent, subject to lawful processing
                  requirements.
                </li>
              </ol>
            </div>
          </div>

          <div *ngIf="submitError" class="alert alert-danger">
            {{ submitError }}
          </div>

          <button type="submit" class="btn btn-primary"
                  [disabled]="applicationForm.invalid || submitting">
            <span *ngIf="submitting" class="spinner-border spinner-border-sm mr-2" role="status"></span>
            {{ submitting ? 'Submitting…' : 'Submit Fibre Application' }}
          </button>
        </form>
      </ng-container>

      <div *ngIf="submitted" class="alert alert-success">
        <h5 class="alert-heading">Application submitted</h5>
        <p class="mb-1">
          Thank you. Your fibre application for
          <strong>{{ submittedPayload.offering.isp }}</strong>
          ({{ submittedPayload.offering.speed }} Mb/s) has been captured.
          The selected ISP will be in touch to arrange your installation.
        </p>
        <p class="mb-0 small text-muted" *ngIf="submittedPayload?.submitted_at">
          Submitted on {{ submittedPayload.submitted_at | date:'medium' }}
        </p>
      </div>

    </section>

  </ng-container>

</div>
